<template>
  <button
    class="custom-btn"
    :class="[
      `custom-btn--${type}`,
      `custom-btn--${size}`,
      { 'is-disabled': disabled, 'is-loading': loading },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="btn-loading"></span>
    <slot></slot>
  </button>
</template>

<script setup>
const emit = defineEmits(["click"]);

const props = defineProps({
  type: {
    type: String,
    default: "default",
    validator: (val) =>
      ["default", "primary", "outline", "text", "danger"].includes(val),
  },
  size: {
    type: String,
    default: "medium",
    validator: (val) => ["small", "medium", "large"].includes(val),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const handleClick = (e) => {
  if (!props.disabled && !props.loading) {
    emit("click", e);
  }
};
</script>

<style scoped lang="less">
.custom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:disabled,
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-loading {
    pointer-events: none;
  }

  .btn-loading {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  // Sizes
  &--small {
    padding: 6px 12px;
    font-size: 12px;
  }

  &--medium {
    padding: 8px 16px;
    font-size: 13px;
  }

  &--large {
    padding: 10px 20px;
    font-size: 14px;
  }

  // Types
  &--default {
    background: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;

    &:hover:not(:disabled) {
      border-color: #c0c4cc;
      color: #409eff;
    }
  }

  &--primary {
    background: #afb68d;
    color: #fff;

    &:hover:not(:disabled) {
      background: #9ea677;
      box-shadow: 0 4px 12px rgba(175, 182, 141, 0.3);
    }
  }

  &--outline {
    background: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;

    &:hover:not(:disabled) {
      border-color: #409eff;
      color: #409eff;
    }
  }

  &--text {
    background: none;
    border: none;
    padding: 4px 8px;
    color: #409eff;

    &:hover:not(:disabled) {
      opacity: 0.7;
    }
  }

  &--danger {
    background: #f56c6c;
    color: #fff;

    &:hover:not(:disabled) {
      background: #f78989;
      box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
    }

    &.custom-btn--text {
      background: none;
      color: #f56c6c;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
